/**
 * Bartender Modal Component
 * NPC bartender on rooftop that sells drinks with particle effects
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { X, Wine, Sparkles, Flame, Snowflake, Zap, Star, Eye } from 'lucide-react';

// Bartender drinks with unique particle effects
const BARTENDER_DRINKS = [
  { 
    id: 'neon_martini', 
    name: 'Neon Martini', 
    icon: '🍸', 
    price: 15, 
    effect: 'glow',
    effectColor: '#39FF14',
    particleType: 'glow',
    duration: 90,
    description: 'Radiate neon green energy'
  },
  { 
    id: 'purple_rain', 
    name: 'Purple Rain', 
    icon: '🍷', 
    price: 20, 
    effect: 'sparkle',
    effectColor: '#9333EA',
    particleType: 'rain',
    duration: 120,
    description: 'Purple particles rain around you'
  },
  { 
    id: 'phoenix_fire', 
    name: 'Phoenix Fire', 
    icon: '🔥', 
    price: 30, 
    effect: 'flames',
    effectColor: '#FF6B35',
    particleType: 'fire',
    duration: 60,
    description: 'Engulf yourself in flames!'
  },
  { 
    id: 'frost_bite', 
    name: 'Frost Bite', 
    icon: '❄️', 
    price: 25, 
    effect: 'ice',
    effectColor: '#67E8F9',
    particleType: 'ice',
    duration: 90,
    description: 'Ice crystals swirl around you'
  },
  { 
    id: 'electric_shock', 
    name: 'Electric Shock', 
    icon: '⚡', 
    price: 35, 
    effect: 'lightning',
    effectColor: '#FACC15',
    particleType: 'electric',
    duration: 45,
    description: 'Lightning bolts crackle around you'
  },
  { 
    id: 'cosmic_dream', 
    name: 'Cosmic Dream', 
    icon: '🌌', 
    price: 50, 
    effect: 'cosmic',
    effectColor: '#EC4899',
    particleType: 'stars',
    duration: 180,
    description: 'Stars and galaxies orbit you'
  },
  { 
    id: 'gold_rush', 
    name: 'Gold Rush', 
    icon: '✨', 
    price: 75, 
    effect: 'gold',
    effectColor: '#FFD700',
    particleType: 'coins',
    duration: 120,
    description: 'Golden coins shower around you'
  },
  { 
    id: 'shadow_elixir', 
    name: 'Shadow Elixir', 
    icon: '🖤', 
    price: 40, 
    effect: 'shadow',
    effectColor: '#1a1a2e',
    particleType: 'smoke',
    duration: 90,
    description: 'Dark smoke billows around you'
  },
  { 
    id: 'rtg_soda', 
    name: 'RTG Flavored Soda', 
    icon: '🍄', 
    price: 100, 
    effect: 'acid_trip',
    effectColor: '#FF00FF',
    particleType: 'psychedelic',
    duration: 300,
    description: 'WARNING: Full psychedelic trip - 5 min of kaleidoscopes & visuals that get heavier and heavier'
  },
  { 
    id: 'turtle_coffee', 
    name: 'Turtle\'s Coffee', 
    icon: '🐢', 
    price: 75, 
    effect: 'turtle_transform',
    effectColor: '#2D6A4F',
    particleType: 'nature',
    duration: 180,
    description: 'Transforms your character into a turtle for 3 minutes!'
  },
];

// Cheaper classics — quick effects for everyone
const HAPPY_HOUR_DRINKS = [
  { id: 'energy_boost', name: 'Energy Boost', icon: '🥤', price: 5, effect: 'speed', effectColor: '#facc15', particleType: 'glow', duration: 30, description: 'A zippy little pick-me-up' },
  { id: 'beach_beer', name: 'Beach Weather Beer', icon: '🍺', price: 8, effect: 'glow', effectColor: '#fbbf24', particleType: 'glow', duration: 60, description: 'Golden and easy-going' },
  { id: 'neon_cocktail', name: 'Neon Cocktail', icon: '🍹', price: 10, effect: 'glow', effectColor: '#00ff88', particleType: 'glow', duration: 60, description: 'A classic glow-up' },
  { id: 'blue_apple_beer', name: 'Blue Apple Beer', icon: '🍏', price: 10, effect: 'sparkle', effectColor: '#38bdf8', particleType: 'rain', duration: 60, description: 'Crisp with a blue shimmer' },
  { id: 'pina_colada', name: 'Piña Colada', icon: '🥥', price: 12, effect: 'glow', effectColor: '#fde68a', particleType: 'glow', duration: 90, description: 'Vacation in a glass' },
  { id: 'purple_haze', name: 'Purple Haze', icon: '🍇', price: 15, effect: 'glow', effectColor: '#a855f7', particleType: 'glow', duration: 90, description: 'A smooth violet aura' },
  { id: 'ice_breaker', name: 'Ice Breaker', icon: '🧊', price: 15, effect: 'sparkle', effectColor: '#67e8f9', particleType: 'ice', duration: 60, description: 'Chill sparkles on demand' },
  { id: 'turtlelini', name: 'Turtlelini Martini', icon: '🫒', price: 15, effect: 'glow', effectColor: '#34d399', particleType: 'glow', duration: 120, description: 'Shelly\'s favorite — emerald glow' },
  { id: 'fire_shot', name: 'Fire Shot', icon: '🌶️', price: 20, effect: 'flames', effectColor: '#ff6b35', particleType: 'fire', duration: 45, description: 'A quick burst of heat' },
  { id: 'golden_elixir', name: 'Golden Elixir', icon: '🏆', price: 50, effect: 'rainbow', effectColor: '#fbbf24', particleType: 'glow', duration: 120, description: 'Shimmer like a champion' },
];

const getEffectIcon = (effect) => {
  switch (effect) {
    case 'flames':
    case 'fire':
      return <Flame className="w-4 h-4" />;
    case 'ice':
      return <Snowflake className="w-4 h-4" />;
    case 'lightning':
    case 'electric':
      return <Zap className="w-4 h-4" />;
    case 'cosmic':
    case 'stars':
      return <Star className="w-4 h-4" />;
    case 'acid_trip':
    case 'psychedelic':
      return <Eye className="w-4 h-4" />;
    default:
      return <Sparkles className="w-4 h-4" />;
  }
};

const RIO_LINES = [
  "Welcome to the Sky Bar! These drinks will make you glow — literally. What can I shake up for you?",
  "Best view in the city, best drinks on the roof. What are you having?",
  "Careful with the RTG Soda... last guy saw colors that don't exist yet.",
  "The Turtle's Coffee? House legend. You WILL grow a shell. Temporarily. Probably.",
  "Happy Hour never ends up here — the sun's always setting somewhere, right?",
  "Fire pit's warm, hot tub's bubbling, and my shaker never sleeps. What'll it be?",
];

const BartenderModal = ({ 
  isOpen, 
  onClose, 
  vibecoins = 0, 
  onPurchase,
  activeDrink 
}) => {
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [rioLine, setRioLine] = useState(RIO_LINES[0]);
  const [rioPick, setRioPick] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setRioLine(RIO_LINES[Math.floor(Math.random() * RIO_LINES.length)]);
      const affordable = [...BARTENDER_DRINKS, ...HAPPY_HOUR_DRINKS].filter(d => d.price <= vibecoins);
      setRioPick(affordable.length ? affordable[Math.floor(Math.random() * affordable.length)] : null);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePurchase = async (drink) => {
    if (vibecoins < drink.price || isPurchasing) return;
    
    setIsPurchasing(true);
    setSelectedDrink(drink);
    
    // Simulate purchase animation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    onPurchase(drink);
    setIsPurchasing(false);
    setSelectedDrink(null);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-b from-amber-900/40 via-gray-900 to-gray-950 rounded-2xl border border-amber-500/30 shadow-2xl overflow-hidden"
        >
          {/* Decorative top bar */}
          <div className="h-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500" />
          
          {/* Header */}
          <div className="p-5 border-b border-amber-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center shadow-lg">
                  <span className="text-2xl">🍾</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Rio — Sky Bar</h2>
                  <p className="text-amber-400/80 text-sm">Signature drinks with visual effects</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-yellow-500/20 px-4 py-2 rounded-full border border-yellow-500/30">
                  <span className="text-yellow-400 text-lg">💰</span>
                  <span className="text-yellow-300 font-bold text-lg">{vibecoins}</span>
                </div>
                <Button
                  onClick={onClose}
                  variant="ghost"
                  size="icon"
                  className="text-white/60 hover:text-white hover:bg-white/10"
                  data-testid="bartender-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Bartender Message */}
          <div className="mx-4 mt-4 p-3 bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl border border-amber-500/20" data-testid="rio-greeting">
            <p className="text-amber-200 text-sm italic">"{rioLine}"</p>
            {rioPick && (
              <p className="text-amber-400/80 text-xs mt-1.5">
                🍸 Rio recommends: <span className="font-semibold text-amber-300">{rioPick.icon} {rioPick.name}</span> — {rioPick.price} VC
              </p>
            )}
          </div>

          {/* Active Effect */}
          {activeDrink && (
            <div className="mx-4 mt-3 p-3 bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-xl border border-purple-500/30">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{activeDrink.icon}</span>
                <div className="flex-1">
                  <p className="text-white font-medium">{activeDrink.name} Active</p>
                  <p className="text-white/60 text-xs">Effect expires in {activeDrink.remainingTime}s</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  {getEffectIcon(activeDrink.effect)}
                </div>
              </div>
            </div>
          )}

          {/* Drinks — grouped menu */}
          <div className="p-4 max-h-[400px] overflow-y-auto space-y-4" data-testid="bartender-drinks-grid" style={{ scrollbarWidth: 'thin' }}>
            {[
              { title: '✨ Signature Effects', items: BARTENDER_DRINKS },
              { title: '🍻 Happy Hour Classics', items: HAPPY_HOUR_DRINKS },
            ].map(({ title, items }) => (
            <div key={title}>
            <p className="text-amber-400/90 text-[11px] font-bold uppercase tracking-widest mb-2">{title}</p>
            <div className="grid grid-cols-2 gap-3">
            {items.map((drink) => {
              const canAfford = vibecoins >= drink.price;
              const isSelected = selectedDrink?.id === drink.id;
              
              return (
                <motion.button
                  key={drink.id}
                  whileHover={{ scale: canAfford ? 1.02 : 1 }}
                  whileTap={{ scale: canAfford ? 0.98 : 1 }}
                  onClick={() => handlePurchase(drink)}
                  disabled={!canAfford || isPurchasing}
                  data-testid={`bartender-drink-${drink.id}`}
                  className={`relative p-4 rounded-xl border text-left transition-all overflow-hidden ${
                    drink.id === 'rtg_soda'
                      ? isSelected
                        ? 'border-fuchsia-500 bg-fuchsia-500/20 scale-95 col-span-2'
                        : canAfford
                        ? 'border-fuchsia-500/50 bg-gradient-to-r from-fuchsia-900/30 to-purple-900/30 hover:from-fuchsia-900/40 hover:to-purple-900/40 hover:border-fuchsia-500/70 col-span-2'
                        : 'border-gray-700/30 bg-black/20 opacity-50 cursor-not-allowed col-span-2'
                    : isSelected
                      ? 'border-amber-500 bg-amber-500/20 scale-95'
                      : canAfford
                      ? 'border-amber-500/30 bg-black/30 hover:bg-amber-500/10 hover:border-amber-500/50'
                      : 'border-gray-700/30 bg-black/20 opacity-50 cursor-not-allowed'
                  }`}
                >
                  {/* Glow effect based on drink color */}
                  <div 
                    className="absolute inset-0 opacity-10 blur-xl"
                    style={{ backgroundColor: drink.effectColor }}
                  />
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-3xl">{drink.icon}</span>
                      <div className="flex items-center gap-1 text-yellow-400 text-sm font-bold bg-black/30 px-2 py-0.5 rounded-full">
                        <span>💰</span>
                        <span>{drink.price}</span>
                      </div>
                    </div>
                    <h3 className="text-white font-medium text-sm">{drink.name}</h3>
                    <p className="text-white/50 text-xs mt-1 line-clamp-2">{drink.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span 
                        className="px-2 py-0.5 rounded-full flex items-center gap-1"
                        style={{ backgroundColor: `${drink.effectColor}30`, color: drink.effectColor }}
                      >
                        {getEffectIcon(drink.particleType)}
                        {drink.effect}
                      </span>
                      <span className="text-white/40">⏱️ {drink.duration}s</span>
                    </div>
                  </div>
                  
                  {/* Purchase animation */}
                  {isSelected && isPurchasing && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 2, opacity: 0 }}
                      className="absolute inset-0 bg-amber-500/50 rounded-xl"
                    />
                  )}
                </motion.button>
              );
            })}
            </div>
            </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-amber-500/20 bg-black/30">
            <p className="text-center text-white/40 text-xs">
              Drink effects are visible to all players on the rooftop
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export { BartenderModal, BARTENDER_DRINKS };
export default BartenderModal;
